'use client';

/**
 * VisitorContextBootstrap
 *
 * Fires once per visitor: if we don't already have a `scw_vctx` cookie,
 * POST-less GET to `/api/visitor-context` which (server-side) reads the
 * HubSpot `hubspotutk` cookie, looks up the contact, and writes the
 * normalized context back as a cookie.
 *
 * After the fetch, dispatches a `scw:visitor-context-updated` event so
 * any mounted `useVisitorContext()` hooks re-render with the new value.
 *
 * Mounted once in the root layout. Idempotent across navigations.
 */

import { useEffect } from 'react';
import {
  VISITOR_CONTEXT_COOKIE,
  readClientCookie,
} from '@/lib/visitor-context';
import { VISITOR_CONTEXT_UPDATED_EVENT } from './useVisitorContext';

export function VisitorContextBootstrap(): null {
  useEffect(() => {
    // Already have the cookie — nothing to do.
    if (readClientCookie(VISITOR_CONTEXT_COOKIE)) return;

    // Defer until the browser is idle so we don't compete with
    // above-the-fold render work.
    const idle = (cb: () => void): number => {
      const w = window as typeof window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
        cancelIdleCallback?: (id: number) => void;
      };
      if (typeof w.requestIdleCallback === 'function') {
        return w.requestIdleCallback(cb, { timeout: 2000 });
      }
      return window.setTimeout(cb, 1200);
    };

    const cancelIdle = (id: number): void => {
      const w = window as typeof window & {
        cancelIdleCallback?: (id: number) => void;
      };
      if (typeof w.cancelIdleCallback === 'function') {
        w.cancelIdleCallback(id);
      } else {
        window.clearTimeout(id);
      }
    };

    let cancelled = false;
    const idleId = idle(() => {
      if (cancelled) return;
      fetch('/api/visitor-context', { credentials: 'same-origin' })
        .then(() => {
          if (cancelled) return;
          window.dispatchEvent(new Event(VISITOR_CONTEXT_UPDATED_EVENT));
        })
        .catch(() => {
          // Non-fatal; smart content falls back to defaults.
        });
    });

    return () => {
      cancelled = true;
      cancelIdle(idleId);
    };
  }, []);

  return null;
}
